import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "./api";
import type { Role, SessionClaims } from "../types";

interface SessionContextValue {
  readonly token: string | undefined;
  readonly signingToken: string | undefined;
  readonly claims: SessionClaims | undefined;
  readonly loading: boolean;
  accept(accessToken: string): void;
  acceptSigning(accessToken: string, signingAccessToken?: string): void;
  preview(): void;
  logout(): Promise<void>;
  hasAnyRole(roles: readonly Role[]): boolean;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const developmentToken = import.meta.env.DEV ? window.localStorage.getItem("dottedline.access-token") ?? undefined : undefined;
  const [token, setToken] = useState<string | undefined>(developmentToken);
  const [signingToken, setSigningToken] = useState<string | undefined>(() => {
    try { return window.sessionStorage.getItem("bluedottedline.signing-token") ?? undefined; }
    catch { return undefined; }
  });
  const [loading, setLoading] = useState(!developmentToken);
  const claims = useMemo(() => token ? decodeClaims(token) : undefined, [token]);

  useEffect(() => {
    if (developmentToken) return;
    let active = true;
    void api.authRefresh().then((result) => { if (active) setToken(result.accessToken); }).catch(() => undefined).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [developmentToken]);

  useEffect(() => {
    if (!claims?.exp || token === "dev-preview") return;
    const delay = Math.max(1_000, claims.exp * 1_000 - Date.now() - 60_000);
    const timer = window.setTimeout(() => {
      void api.authRefresh().then((result) => setToken(result.accessToken)).catch(() => setToken(undefined));
    }, delay);
    return () => window.clearTimeout(timer);
  }, [claims?.exp, token]);

  const value = useMemo<SessionContextValue>(() => ({
    token,
    signingToken,
    claims,
    loading,
    accept(accessToken) { decodeClaims(accessToken); setToken(accessToken); setSigningToken(undefined); try { window.sessionStorage.removeItem("bluedottedline.signing-token"); } catch { /* Session storage can be unavailable in privacy mode. */ } },
    acceptSigning(accessToken, scopedSigningToken = accessToken) { decodeClaims(accessToken); decodeClaims(scopedSigningToken); setToken(accessToken); setSigningToken(scopedSigningToken); try { window.sessionStorage.setItem("bluedottedline.signing-token", scopedSigningToken); } catch { /* Session storage can be unavailable in privacy mode. */ } },
    preview() { if (import.meta.env.DEV) setToken("dev-preview"); },
    async logout() { try { if (token !== "dev-preview") await api.authLogout(); } finally { setToken(undefined); setSigningToken(undefined); try { window.sessionStorage.removeItem("bluedottedline.signing-token"); window.sessionStorage.removeItem("bluedottedline.signing-invitation-token"); } catch { /* Session storage can be unavailable in privacy mode. */ } } },
    hasAnyRole(roles) { return Boolean(claims?.roles.some((role) => roles.includes(role))); },
  }), [claims, loading, signingToken, token]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("SessionProvider is required.");
  return value;
}

function decodeClaims(token: string): SessionClaims {
  if (token === "dev-preview" && import.meta.env.DEV) return { sub: "preview", tenant_id: "preview", roles: ["owner", "admin", "designer", "sender", "signer", "auditor"], token_use: "access", name: "Mara Voss", email: "mara@dottedline.test" };
  const payload = token.split(".")[1];
  if (!payload) throw new Error("The access token is malformed.");
  const normalized = payload.replaceAll("-", "+").replaceAll("_", "/").padEnd(Math.ceil(payload.length / 4) * 4, "=");
  const claims = JSON.parse(atob(normalized)) as SessionClaims;
  if (!claims.sub || !claims.tenant_id || claims.token_use !== "access" || !Array.isArray(claims.roles) || !claims.roles.length) throw new Error("The access token has invalid claims.");
  return claims;
}
